import {
	FontAwesome,
	MaterialCommunityIcons,
	MaterialIcons
} from "@expo/vector-icons";
import { useNavigation } from "@react-navigation/core";
import { useHeaderHeight } from "@react-navigation/elements";
import React, { memo, useEffect, useRef, useState } from "react";
import {
	ActivityIndicator,
	Keyboard,
	KeyboardAvoidingView,
	ScrollView,
	Text,
	TouchableOpacity,
	View
} from "react-native";
import { Avatar, CheckBox, Input } from "react-native-elements";
import { TextInputMask } from "react-native-masked-text";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { fetchDeleteUser, fetchUnregisterUser } from "../actions/login";
import { fetchUpdateProfile } from "../actions/profile";
import {
	CLEAR_PHOTO,
	CLEAR_PROFILE_VALIDATE,
	EDIT_PROFILE,
	HIDE_SPLASH_SCREEN,
	LOGOUT,
	NOT_UPDATE_AUTOMATICALLY_HOBBIES,
	REGISTER_FOR_PUSH_NOTIF,
	SET_APARTMENT_NUMBER,
	SET_BIRTHDATE,
	SET_BUILDING_ACCESS,
	SET_DESCRIPTION,
	SET_FIRST_NAME,
	SET_FLOOR,
	SET_GENDER,
	SET_JOB,
	SET_LAST_NAME,
	SET_PHONE,
	SET_PHOTO_URI,
	SET_USER_HOBBIES,
	SET_VALIDATE_FALSE,
	TOGGLE_CONFIRM_ADDRESS,
	TOGGLE_CONFIRM_MIN_AGE,
	UPDATE_AUTOMATICALLY_HOBBIES,
	VALIDATE_PROFILE_INPUTS
} from "../actions/types";
import ActionButton from "../components/actionButton/ActionButton";
import ConfirmationModal from "../components/ConfirmationModal";
import Hobbies from "../components/Hobbies";
import { ROLE_NEIGHBOR } from "../config/constants";
import { getProfileUpdateSelector } from "../selectors";
import { t } from "../services/i18n";
import commonStyles, {
	BLUE_COLOR,
	BROWN_COLOR,
	COLOR2,
	COLOR5,
	isiOS,
	isNative,
	isWeb,
	PINK_COLOR,
	SCREEN_HEIGHT
} from "../styles/commonStyles";
import { getAvatarSource } from "../utils/AvatarUtil";
import { pickImage } from "../utils/PickImage";

const IMAGE_SIZE = 200;

const ProfileUpdate = () => {
	const headerHeight = useHeaderHeight();
	const firstnameInputRef = useRef();
	const lastnameInputRef = useRef();
	const confirmationModal = useRef();
	const confirmationLeaveModal = useRef();
	const insets = useSafeAreaInsets();

	const navigation = useNavigation();
	const dispatch = useDispatch();

	const scrollViewRef = useRef();

	// Y positions captured via onLayout, used to scroll to the first invalid
	// field on submit. `cardY` holds each card's offset within the scroll
	// content; `fieldY` holds each field's offset within its card. We avoid
	// measureLayout (unreliable on the new RN architecture / collapsed Android
	// views); onLayout is robust and also prevents view collapsing.
	const cardY = useRef({});
	const fieldY = useRef({});
	// True between pressing submit and the validation result, so we only
	// auto-scroll on an explicit submit (not while the user edits fields).
	const submitAttemptRef = useRef(false);

	const [isLoading, setIsLoading] = useState(true);

	const {
		files,
		pseudo,
		firstname,
		lastname,
		gender,
		birthdate,
		address,
		zipCode,
		city,
		phone,
		description,
		photoUri,
		hobbiesIds,
		firstnameErrorMsg,
		lastnameErrorMsg,
		birthdayErrorMsg,
		confirmAddressErrorMsg,
		confirmMinAgeErrorMsg,
		confirmMinAge,
		confirmAddress,
		user,
		isValid,
		isFetching,
		firstLogin,
		clearProfileValidate,
		phoneErrorMsg,
		floor,
		apartmentNumber,
		job,
		building,
		buildingAccess,
		residence,
		district,
		needRegisterForPush,
		updateHobbies,
		organizationId
	} = useSelector(getProfileUpdateSelector, shallowEqual);

	const { role, needUpdate = false } = user || {};

	useEffect(() => {
		dispatch({
			type: EDIT_PROFILE
		});
		if (isNative) {
			dispatch({
				type: HIDE_SPLASH_SCREEN
			});
		}
		setIsLoading(false);
	}, []);

	useEffect(() => {
		if (files) {
			firstnameInputRef.currrent?.focus();
		}
	}, [files]);

	useEffect(() => {
		if (clearProfileValidate) {
			dispatch({
				type: VALIDATE_PROFILE_INPUTS
			});
		}
	}, [clearProfileValidate]);

	const scrollToFirstError = () => {
		// Validatable fields, top to bottom. `card` is the card the field lives in;
		// `inputRef` is set only for fields that can take keyboard focus.
		const fields = [
			{
				msg: firstnameErrorMsg,
				key: "firstname",
				card: "card1",
				inputRef: firstnameInputRef
			},
			{
				msg: lastnameErrorMsg,
				key: "lastname",
				card: "card1",
				inputRef: lastnameInputRef
			},
			{ msg: birthdayErrorMsg, key: "birthdate", card: "card1" },
			{ msg: confirmMinAgeErrorMsg, key: "confirmMinAge", card: "card1" },
			{ msg: confirmAddressErrorMsg, key: "confirmAddress", card: "card2" },
			{ msg: phoneErrorMsg, key: "phone", card: "card3" }
		];
		const first = fields.find(field => field.msg);
		if (!first || !scrollViewRef.current) {
			return;
		}
		const y =
			(cardY.current[first.card] || 0) + (fieldY.current[first.key] || 0);
		scrollViewRef.current.scrollTo({ y: Math.max(y - 24, 0), animated: true });
		if (first.inputRef?.current?.focus) {
			setTimeout(() => first.inputRef.current.focus(), 350);
		}
	};

	useEffect(() => {
		// After a submit-triggered validation completes (clearProfileValidate goes
		// back to false), scroll to the first invalid field if the form is invalid.
		if (!clearProfileValidate && submitAttemptRef.current) {
			submitAttemptRef.current = false;
			if (!isValid) {
				scrollToFirstError();
			}
		}
	}, [clearProfileValidate]);

	useEffect(() => {
		if (isValid) {
			dispatch(
				fetchUpdateProfile({
					photoUri: photoUri && !files.length ? photoUri : null,
					firstname,
					lastname,
					gender,
					address,
					zipCode,
					files,
					city,
					phone,
					description,
					birthdate,
					floor,
					apartmentNumber,
					job,
					updateHobbies,
					hobbiesIds: [...hobbiesIds],
					buildingAccess
				})
			);
		}
	}, [isValid]);

	useEffect(() => {
		if (isValid && user) {
			navigation.navigate("TabScreen");
			if (needRegisterForPush) {
				dispatch({
					type: REGISTER_FOR_PUSH_NOTIF
				});
			}
			dispatch({
				type: SET_VALIDATE_FALSE
			});
		}
	}, [user]);

	const deleteUser = () => {
		dispatch(fetchDeleteUser());
	};

	const leaveCommunity = () => {
		dispatch(fetchUnregisterUser());
	};

	const _pickImage = async () => {
		const image = await pickImage(dispatch);

		if (image) {
			dispatch({
				type: SET_PHOTO_URI,
				...image
			});
		}
	};

	const cancel = () => {
		if (needUpdate) {
			dispatch({
				type: LOGOUT
			});
		} else if (navigation.canGoBack()) {
			navigation.goBack();
		} else {
			// No back stack (e.g. page refresh / direct URL on web): return to the
			// main app instead of doing nothing.
			navigation.navigate("TabScreen");
		}
	};

	const {
		red,
		bgColor1,
		darkgrey,
		bgWhite,
		color2,
		color5,
		flex1,
		alignItemsCenter,
		justifyContentCenter,
		fs12,
		fs16,
		fs18,
		fs20,
		row,
		p10,
		p20,
		ml10,
		mt10,
		mb10,
		mb20,
		mv10,
		mv20,
		font,
		alignSelfCenter,
		fontBold,
		shadowGrey,
		w100p,
		ml5,
		mt5,
		gap10
	} = commonStyles;

	const renderStickyButtons = () => {
		return (
			<View
				style={[row, { position: "absolute", bottom: 0, left: 0, right: 0 }]}
			>
				<TouchableOpacity
					underlayColor="transparent"
					activeOpacity={0.8}
					onPress={cancel}
					style={[
						{
							height: 60 + insets.bottom,
							flex: 1,
							justifyContent: "center",
							alignItems: "center",
							backgroundColor: "white"
						}
					]}
				>
					<Text style={[fs16, fontBold, red]}>{t("button:cancel")}</Text>
				</TouchableOpacity>
				<TouchableOpacity
					underlayColor="transparent"
					activeOpacity={0.8}
					onPress={() => {
						submitAttemptRef.current = true;
						dispatch({
							type: CLEAR_PROFILE_VALIDATE
						});
					}}
					disabled={isFetching}
					style={[
						{
							height: 60 + insets.bottom,
							flex: 1,
							justifyContent: "center",
							alignItems: "center",
							backgroundColor: COLOR2,
							borderLeftWidth: 1,
							borderLeftColor: "#e0e0e0"
						}
					]}
				>
					<Text style={[fontBold, fs16, { color: "white" }]}>
						{firstLogin ? t("button:signup") : t("button:update")}
					</Text>
				</TouchableOpacity>
			</View>
		);
	};

	return (
		<KeyboardAvoidingView
			{...(isiOS && { behavior: "padding" })}
			style={[[w100p, { height: SCREEN_HEIGHT - headerHeight }, bgColor1]]}
		>
			<ConfirmationModal
				ref={confirmationModal}
				description={t("settings:confirmdelete")}
				title={t("menu:confirmation")}
				onConfirmation={deleteUser}
			/>
			<ConfirmationModal
				ref={confirmationLeaveModal}
				description={t("settings:confirmleave")}
				title={t("menu:confirmation")}
				onConfirmation={leaveCommunity}
				buttonConfirm={t("button:unsubscribe")}
			/>
			{isFetching || isLoading ? (
				<ActivityIndicator
					style={[flex1, justifyContentCenter]}
					size="large"
					color={COLOR2}
				/>
			) : (
				<View style={[flex1]}>
					<ScrollView
						ref={scrollViewRef}
						style={[flex1]}
						contentContainerStyle={{ paddingBottom: 90 }}
						showsVerticalScrollIndicator={false}
					>
						<View
							onLayout={e => (cardY.current.card1 = e.nativeEvent.layout.y)}
							style={[
								w100p,
								p10,
								bgWhite,
								mt10,
								mb20,
								shadowGrey,
								alignItemsCenter
							]}
						>
							<Text style={[fontBold, color5, fs20, mv20]}>
								{t("profile:profile")}
							</Text>

							<View style={[row, mb20, justifyContentCenter, w100p]}>
								<Avatar
									rounded
									size={IMAGE_SIZE}
									title={pseudo ? pseudo.toUpperCase().substr(0, 2) : "?"}
									source={getAvatarSource(photoUri, gender)}
									onPress={() => {
										Keyboard.dismiss();
										navigation.navigate("CameraScreen", {
											from: "profile"
										});
									}}
								/>
								<ActionButton
									buttonColor={COLOR5}
									renderIcon={() => (
										<MaterialCommunityIcons
											name="dots-horizontal"
											size={34}
											color="white"
										/>
									)}
									verticalOrientation="down"
									size={44}
									offsetX={10}
									offsetY={0}
									degrees={90}
									spacing={10}
								>
									{Boolean(photoUri) && (
										<ActionButton.Item
											buttonColor={"red"}
											title={t("button:delete")}
											onPress={() => dispatch({ type: CLEAR_PHOTO })}
											textStyle={font}
											textHeight={24}
										>
											<MaterialCommunityIcons
												name={"delete-forever"}
												size={34}
												color="white"
											/>
										</ActionButton.Item>
									)}
									<ActionButton.Item
										buttonColor={COLOR2}
										title={t("button:albums")}
										onPress={_pickImage}
										textStyle={font}
										textHeight={24}
									>
										<FontAwesome name="image" size={28} color="white" />
									</ActionButton.Item>
									<ActionButton.Item
										buttonColor={COLOR2}
										title={t("button:camera")}
										onPress={() => {
											Keyboard.dismiss();
											navigation.navigate("CameraScreen", {
												from: "profile"
											});
										}}
										textStyle={font}
										textHeight={24}
									>
										<MaterialIcons name="camera-alt" size={34} color="white" />
									</ActionButton.Item>
								</ActionButton>
							</View>
							<View style={[mv10, row, alignItemsCenter]}>
								<CheckBox
									title={"?"}
									textStyle={[{ color: BROWN_COLOR }, fontBold]}
									checkedColor={BROWN_COLOR}
									uncheckedColor={BROWN_COLOR}
									checkedIcon="dot-circle-o"
									uncheckedIcon="circle-o"
									checked={!gender}
									onPress={() => dispatch({ type: SET_GENDER, value: null })}
								/>
								<CheckBox
									title={t("profile:male")}
									textStyle={[{ color: BLUE_COLOR }, fontBold]}
									checkedColor={BLUE_COLOR}
									uncheckedColor={BLUE_COLOR}
									checkedIcon="dot-circle-o"
									uncheckedIcon="circle-o"
									checked={gender === "M"}
									onPress={() => dispatch({ type: SET_GENDER, value: "M" })}
								/>
								<CheckBox
									title={t("profile:female")}
									textStyle={[{ color: PINK_COLOR }, fontBold]}
									checkedColor={PINK_COLOR}
									uncheckedColor={PINK_COLOR}
									checkedIcon="dot-circle-o"
									uncheckedIcon="circle-o"
									checked={gender === "F"}
									onPress={() => dispatch({ type: SET_GENDER, value: "F" })}
								/>
							</View>
							<View
								onLayout={e =>
									(fieldY.current.firstname = e.nativeEvent.layout.y)
								}
								style={w100p}
							>
								<Input
									ref={firstnameInputRef}
									inputStyle={[isiOS ? ml10 : null, darkgrey, font]}
									onChangeText={value =>
										dispatch({ type: SET_FIRST_NAME, value })
									}
									placeholder={t("profile:firstname")}
									keyboardType="default"
									autoCorrect={false}
									autoCapitalize="words"
									keyboardAppearance="light"
									returnKeyType="next"
									value={firstname}
									errorMessage={firstnameErrorMsg}
								/>
							</View>
							<View
								onLayout={e =>
									(fieldY.current.lastname = e.nativeEvent.layout.y)
								}
								style={w100p}
							>
								<Input
									ref={lastnameInputRef}
									inputStyle={[isiOS ? ml10 : null, darkgrey, font]}
									onChangeText={value =>
										dispatch({ type: SET_LAST_NAME, value })
									}
									containerStyle={mb20}
									placeholder={t("profile:lastname")}
									autoCorrect={false}
									autoCapitalize="words"
									value={lastname}
									keyboardType="default"
									returnKeyType="next"
									errorMessage={lastnameErrorMsg}
								/>
							</View>

							{role === ROLE_NEIGHBOR && (
								<View
									onLayout={e =>
										(fieldY.current.birthdate = e.nativeEvent.layout.y)
									}
									style={[{ paddingHorizontal: 10 }, w100p]}
								>
									<TextInputMask
										type={isWeb ? "custom" : "datetime"}
										options={
											isWeb
												? {
														mask: "99/99/9999"
												  }
												: {
														format: "DD/MM/YYYY"
												  }
										}
										placeholder={t("profile:birthdate")}
										value={birthdate}
										style={[
											font,
											{
												paddingLeft: isiOS ? 10 : 0,
												paddingBottom: 10,
												fontSize: 18,
												height: 40,
												width: "100%",
												borderColor: "gray",
												borderBottomWidth: 1
											},
											!birthdayErrorMsg && mb20
										]}
										onChangeText={value =>
											dispatch({ type: SET_BIRTHDATE, value })
										}
									/>
									{birthdayErrorMsg && (
										<Text style={[ml5, mt5, red, fs12, mb20]}>
											{birthdayErrorMsg}
										</Text>
									)}
								</View>
							)}

							{role === ROLE_NEIGHBOR && !birthdate && (
								<View
									onLayout={e =>
										(fieldY.current.confirmMinAge = e.nativeEvent.layout.y)
									}
									style={w100p}
								>
									<CheckBox
										title={t("profile:confirmage")}
										textStyle={[darkgrey, fs18, fontBold]}
										checkedColor={COLOR2}
										containerStyle={!confirmMinAgeErrorMsg && mb20}
										checked={confirmMinAge}
										onPress={() => dispatch({ type: TOGGLE_CONFIRM_MIN_AGE })}
									/>
								</View>
							)}
							{confirmMinAgeErrorMsg && (
								<Text style={[ml5, mt5, red, fs12, mb20]}>
									{confirmMinAgeErrorMsg}
								</Text>
							)}
						</View>
						{role === ROLE_NEIGHBOR && (
							<View
								onLayout={e => (cardY.current.card2 = e.nativeEvent.layout.y)}
								style={[
									w100p,
									p10,
									bgWhite,
									mt10,
									mb20,
									shadowGrey,
									alignItemsCenter
								]}
							>
								<Text style={[fontBold, color2, fs20, mv20]}>
									{t("profile:address")}
								</Text>
								<Text style={[font, darkgrey, fs18, mb10]}>{address}</Text>
								<Text
									style={[font, darkgrey, fs18, mb10]}
								>{`${zipCode} ${city}`}</Text>
								{Boolean(building) && (
									<Text
										style={[
											font,
											darkgrey,
											fs18,
											Boolean(building || district) ? mb10 : mb20
										]}
									>{`${t("infos:building")} ${building}`}</Text>
								)}
								{Boolean(residence) && (
									<Text
										style={[
											font,
											darkgrey,
											fs18,
											Boolean(district) ? mb10 : mb20
										]}
									>{`${t("infos:residence")} ${residence}`}</Text>
								)}
								{Boolean(district) && (
									<Text style={[font, darkgrey, fs18, mb20]}>{`${t(
										"infos:district"
									)} ${district}`}</Text>
								)}
								<View
									onLayout={e =>
										(fieldY.current.confirmAddress = e.nativeEvent.layout.y)
									}
									style={w100p}
								>
									<CheckBox
										title={t("profile:confirmaddress")}
										textStyle={[darkgrey, fs18, fontBold]}
										checkedColor={COLOR2}
										containerStyle={!confirmAddressErrorMsg && mb20}
										checked={confirmAddress}
										onPress={() => dispatch({ type: TOGGLE_CONFIRM_ADDRESS })}
									/>
								</View>
								{confirmAddressErrorMsg && (
									<Text style={[ml5, mt5, red, fs12, mb20]}>
										{confirmAddressErrorMsg}
									</Text>
								)}
							</View>
						)}

						{role === ROLE_NEIGHBOR && (
							<View
								onLayout={e => (cardY.current.card3 = e.nativeEvent.layout.y)}
								style={[
									w100p,
									p10,
									bgWhite,
									mt10,
									mb20,
									shadowGrey,
									alignItemsCenter
								]}
							>
								<Text style={[fontBold, color2, fs20, mv20]}>
									{t("profile:moreinfos")}
								</Text>
								<Input
									inputStyle={[isiOS ? ml10 : null, darkgrey, font]}
									containerStyle={mb10}
									onChangeText={value =>
										dispatch({ type: SET_BUILDING_ACCESS, value })
									}
									placeholder={t("profile:buildingaccess")}
									keyboardAppearance="light"
									returnKeyType="next"
									value={buildingAccess}
								/>
								<Input
									inputStyle={[isiOS ? ml10 : null, darkgrey, font]}
									containerStyle={mb10}
									onChangeText={value =>
										dispatch({ type: SET_APARTMENT_NUMBER, value })
									}
									placeholder={t("profile:apartmentnumber")}
									keyboardType="decimal-pad"
									keyboardAppearance="light"
									returnKeyType="next"
									value={apartmentNumber}
								/>
								<Input
									inputStyle={[isiOS ? ml10 : null, darkgrey, font]}
									containerStyle={mb20}
									onChangeText={value => dispatch({ type: SET_FLOOR, value })}
									placeholder={t("profile:floor")}
									keyboardType="decimal-pad"
									keyboardAppearance="light"
									returnKeyType="next"
									value={floor}
								/>
								<View
									onLayout={e =>
										(fieldY.current.phone = e.nativeEvent.layout.y)
									}
									style={[{ paddingHorizontal: 10 }, w100p, mb10]}
								>
									<TextInputMask
										type={"custom"}
										options={{
											mask: "+33 (0)9 99 99 99 99"
										}}
										keyboardType="phone-pad"
										placeholder={t("profile:phone")}
										value={phone}
										style={[
											font,
											darkgrey,
											{
												paddingLeft: isiOS ? 10 : 0,
												paddingBottom: 10,
												fontSize: 18,
												height: 40,
												width: "100%",
												borderColor: "gray",
												borderBottomWidth: 1
											},
											!birthdayErrorMsg && mb20
										]}
										onChangeText={value => dispatch({ type: SET_PHONE, value })}
									/>
									{phoneErrorMsg && (
										<Text style={[ml5, mt5, red, fs12]}>{phoneErrorMsg}</Text>
									)}
								</View>
								<Input
									inputStyle={[isiOS ? ml10 : null, darkgrey, font]}
									containerStyle={mb10}
									onChangeText={value => dispatch({ type: SET_JOB, value })}
									placeholder={t("profile:job")}
									keyboardType="default"
									keyboardAppearance="light"
									returnKeyType="next"
									value={job}
								/>
								<Input
									inputStyle={[isiOS ? ml10 : null, darkgrey, font]}
									containerStyle={mb20}
									onChangeText={value =>
										dispatch({ type: SET_DESCRIPTION, value })
									}
									placeholder={t("profile:description")}
									keyboardType="default"
									keyboardAppearance="light"
									returnKeyType="next"
									value={description}
								/>
							</View>
						)}
						{role === ROLE_NEIGHBOR && (
							<View
								style={[
									w100p,
									p20,
									bgWhite,
									mt10,
									mb20,
									shadowGrey,
									alignItemsCenter
								]}
							>
								<Text style={[fontBold, color5, fs20, mv20]}>
									{t("profile:hobbies")}
								</Text>
								<Hobbies
									hobbiesIds={new Set(hobbiesIds)}
									onHobbiesChange={value =>
										dispatch({ type: SET_USER_HOBBIES, value })
									}
								/>
								<CheckBox
									title={t("profile:updatehobbies")}
									textStyle={[darkgrey, fontBold]}
									containerStyle={[w100p]}
									checkedColor={COLOR2}
									uncheckedColor={COLOR2}
									checkedIcon="dot-circle-o"
									uncheckedIcon="circle-o"
									checked={updateHobbies}
									onPress={() =>
										dispatch({ type: UPDATE_AUTOMATICALLY_HOBBIES })
									}
								/>
								<CheckBox
									title={t("profile:noupdatehobbies")}
									textStyle={[darkgrey, fontBold]}
									containerStyle={[w100p]}
									checkedColor={COLOR2}
									uncheckedColor={COLOR2}
									checkedIcon="dot-circle-o"
									uncheckedIcon="circle-o"
									checked={!updateHobbies}
									onPress={() =>
										dispatch({ type: NOT_UPDATE_AUTOMATICALLY_HOBBIES })
									}
								/>
							</View>
						)}
						{(needUpdate || role === ROLE_NEIGHBOR) && (
							<TouchableOpacity
								style={[row, gap10, alignSelfCenter, mb20]}
								onPress={() => confirmationModal.current?.open()}
							>
								<MaterialCommunityIcons
									name="account-remove"
									size={22}
									color="red"
								/>
								<Text style={[fontBold, fs16, red]}>
									{t("button:deleteaccount")}
								</Text>
							</TouchableOpacity>
						)}
						{((role !== ROLE_NEIGHBOR && !organizationId) ||
							role === ROLE_NEIGHBOR) && (
							<TouchableOpacity
								style={[row, gap10, alignSelfCenter, mb20]}
								onPress={() => confirmationLeaveModal.current?.open()}
							>
								<MaterialCommunityIcons name="exit-run" size={22} color="red" />
								<Text style={[fontBold, fs16, red]}>
									{t("button:leavecommunity")}
								</Text>
							</TouchableOpacity>
						)}
					</ScrollView>
					{renderStickyButtons()}
				</View>
			)}
		</KeyboardAvoidingView>
	);
};

const MemoizedProfileUpdate = memo(ProfileUpdate);
export default MemoizedProfileUpdate;
